import { t } from '@lingui/core/macro';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';

import { notify } from '@/components/base/notifications/notifications';
import { queryKeys } from '@/hooks/api/query-keys';
import { useAnalytics } from '@/lib/analytics/use-analytics';
import { clearLinkedinVolIdentityProfileIdCookie } from '@/utils/linkedin-vol-return-cookie';

function parseSearchStr(searchStr: string): URLSearchParams {
  const trimmed = searchStr.trim();
  const withoutQ = trimmed.startsWith('?') ? trimmed.slice(1) : trimmed;
  return new URLSearchParams(withoutQ);
}

/**
 * LinkedIn Verify lands the user with `linkedin_vol` / `reason` on the URL.
 * This hook shows success/failure toasts, refetches profile, then strips those query params.
 * Return-to-project is handled in the server callback using a short-lived cookie set before OAuth.
 */
export function useLinkedinVolOAuthReturn(userId?: string) {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const analytics = useAnalytics();

  // Strict-Mode + back-button guard. The effect below fires the completed
  // / failed analytics events synchronously before navigating to strip the
  // query params; in dev Strict Mode the effect runs twice on mount before
  // that navigation lands, producing duplicate funnel events. A revisit via
  // back-button to the same OAuth-return URL would also re-fire. Keyed on
  // the raw searchStr so both cases collapse to a single fire.
  const firedForSearchRef = useRef<string | null>(null);

  useEffect(() => {
    if (!userId) return;

    const params = parseSearchStr(location.searchStr ?? '');
    const status = params.get('linkedin_vol');

    // Should never happen but just incase
    if (status !== 'connected' && status !== 'error') {
      return;
    }

    const currentSearch = location.searchStr ?? '';
    if (firedForSearchRef.current === currentSearch) return;
    firedForSearchRef.current = currentSearch;

    const reason = params.get('reason') ?? undefined;

    void (async () => {
      // The identity journey is over either way — drop its profile-id cookie.
      clearLinkedinVolIdentityProfileIdCookie();

      if (status === 'connected') {
        analytics?.track({
          action: 'completed',
          actionSubject: 'linkedinVerification',
        });
        notify({ title: t`LinkedIn verified`, color: 'success' });
        await queryClient.invalidateQueries({
          queryKey: queryKeys.profile.byUserId(userId),
        });
        // The server may have recalculated outstanding pre-submission steps
        // (LinkedIn government-ID verification can drop the plaid step) —
        // refetch so the apply page advances without a manual refresh.
        await queryClient.invalidateQueries({
          queryKey: queryKeys.projects.screenerBase,
        });
        await queryClient.invalidateQueries({
          queryKey: queryKeys.plaidVerificationLinkToken.all,
        });
      } else if (
        reason === 'identity_unavailable' ||
        reason === 'identity_not_verified' ||
        reason === 'identity_return_expired'
      ) {
        // The LinkedIn connection itself succeeded; only the government-ID
        // check didn't complete. Eligible members must finish on LinkedIn —
        // retrying routes there again (no Plaid fallback, by design); only
        // members LinkedIn deems ineligible drop to Plaid on retry.
        analytics?.track({
          action: 'failed',
          actionSubject: 'linkedinIdVerification',
          attributes: { reason },
        });
        const identityDescriptions: Record<string, string> = {
          identity_unavailable: t`LinkedIn ID verification isn't available for your account. Please verify your identity below instead.`,
          identity_not_verified: t`We couldn't confirm an ID verification on your LinkedIn account. Please try again to continue your application.`,
          identity_return_expired: t`Your LinkedIn verification session expired. Please try again.`,
        };
        notify({
          title: t`LinkedIn ID verification not completed`,
          description: identityDescriptions[reason],
          color: 'error',
        });
        await queryClient.invalidateQueries({
          queryKey: queryKeys.profile.byUserId(userId),
        });
        await queryClient.invalidateQueries({
          queryKey: queryKeys.projects.screenerBase,
        });
        await queryClient.invalidateQueries({
          queryKey: queryKeys.plaidVerificationLinkToken.all,
        });
      } else {
        analytics?.track({
          action: 'failed',
          actionSubject: 'linkedinVerification',
          attributes: { reason },
        });
        const description =
          reason === 'already_verified_elsewhere'
            ? t`This LinkedIn account is already verified on another Respondent profile.`
            : reason || undefined;
        notify({
          title: t`LinkedIn verification failed`,
          description,
          color: 'error',
        });
      }

      const nextParams = parseSearchStr(location.searchStr ?? '');
      nextParams.delete('linkedin_vol');
      nextParams.delete('reason');
      const qs = nextParams.toString();
      const target = `${location.pathname}${qs ? `?${qs}` : ''}`;
      void navigate({ to: target, replace: true });
    })();
  }, [
    analytics,
    location.pathname,
    location.searchStr,
    navigate,
    queryClient,
    userId,
  ]);
}
